import NumberFormatCustom, { NumberFormatCustomOnChange } from '@components/NumberFormatCustom';
import {
  InputAdornment,
  InputBaseComponentProps,
  TextField as MuiTextField,
  TextFieldProps,
} from '@mui/material';
import { ChangeEvent, ElementType, forwardRef } from 'react';
import useMoney from '../../../../hooks/useMoney';

export interface MoneyFieldProps
  extends Pick<
    TextFieldProps,
    | 'label'
    | 'error'
    | 'helperText'
    | 'disabled'
    | 'size'
    | 'name'
    | 'required'
    | 'fullWidth'
    | 'placeholder'
    | 'onBlur'
  > {
  value: number | null;
  onChange: (value: number | null) => void;
  allowNegative?: boolean;
  testId?: string;
}

const MoneyField = forwardRef<HTMLInputElement, MoneyFieldProps>(
  (
    {
      value,
      onChange,
      label,
      error,
      helperText,
      disabled,
      size = 'medium',
      name,
      required,
      fullWidth = true,
      allowNegative = false,
      placeholder,
      testId,
      onBlur,
    },
    ref,
  ) => {
    const { getCurrencySymbol, thousandSeparator, decimalSeparator } = useMoney();

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { target } = event as unknown as NumberFormatCustomOnChange;

      onChange(target.value);
    };

    return (
      <MuiTextField
        variant="outlined"
        size={size}
        name={name}
        label={label}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        fullWidth={fullWidth}
        inputRef={ref}
        value={value ?? ''}
        onChange={handleChange}
        onBlur={onBlur}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment>,
            inputComponent: NumberFormatCustom as unknown as ElementType<InputBaseComponentProps>,
          },
          htmlInput: {
            allowNegative,
            thousandSeparator,
            decimalSeparator,
            decimalScale: 2,
            fixedDecimalScale: true,
            'test-id': testId,
          },
        }}
      />
    );
  },
);

MoneyField.displayName = 'MoneyField';

export default MoneyField;
